import { type ReactNode } from 'react';
import { SignedIn, SignedOut, useClerk } from '@clerk/clerk-react';
import { isClerkEnabled } from '@/components/auth-gate';
import { AccountMenu } from '@/components/account-menu';
import {
  getMarketingPrimaryCta,
  getSignUpPrimaryCta,
  marketingCtaAttribution,
  type MarketingPrimaryCtaConfig,
} from '@/lib/marketing-cta';
import { appUrl } from '@/lib/brand';
import { capture } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n';
import { Button, buttonVariants } from '@/components/ui';

export type MarketingAccountCtaVariant = 'marketing' | 'studio' | 'hero';
export type MarketingAccountCtaSource =
  | 'marketing_header'
  | 'studio_landing'
  | 'homepage_hero'
  | 'homepage_closing';

const VARIANT_CLASS: Record<MarketingAccountCtaVariant, string> = {
  marketing: buttonVariants({ size: 'default' }),
  studio: buttonVariants({ size: 'sm' }),
  hero: buttonVariants({ size: 'lg' }),
};

function trackPrimaryCta(
  cta: MarketingPrimaryCtaConfig,
  source: MarketingAccountCtaSource,
  surface?: 'public' | 'app',
) {
  const attribution = marketingCtaAttribution(cta);
  if (source === 'studio_landing') {
    capture(
      'studio_cta_clicked',
      {
        cta: cta.variant,
        destination: cta.href,
        surface: surface ?? 'public',
        ...attribution,
      },
      { transport: 'sendBeacon', send_instantly: true },
    );
    return;
  }
  if (source === 'homepage_hero' || source === 'homepage_closing') {
    const slot = source === 'homepage_hero' ? 'hero' : 'footer';
    capture('marketing_home_cta_clicked', {
      cta: cta.variant === 'download' ? `${slot}_download` : `${slot}_sign_up`,
      destination: cta.href,
      ...attribution,
    });
    return;
  }
  capture('marketing_nav_click', {
    destination: cta.variant,
    href: cta.href,
    ...attribution,
  });
}

function PrimaryCtaLabel({ cta }: { cta: MarketingPrimaryCtaConfig }) {
  const { t } = useTranslation();
  return cta.variant === 'sign_up'
    ? t('shell.nav.signUp', { defaultValue: cta.label })
    : t('shell.nav.downloadDesktopApps', { defaultValue: cta.label });
}

function PrimaryCtaAnchor({
  cta,
  variant,
  source,
  surface,
  trailing,
}: {
  cta: MarketingPrimaryCtaConfig;
  variant: MarketingAccountCtaVariant;
  source: MarketingAccountCtaSource;
  surface?: 'public' | 'app';
  trailing?: ReactNode;
}) {
  return (
    <a
      href={cta.href}
      onClick={() => trackPrimaryCta(cta, source, surface)}
      className={VARIANT_CLASS[variant]}
      data-okra-cta-variant={cta.variant}
      data-okra-cta-source={source}
    >
      <PrimaryCtaLabel cta={cta} />
      {trailing}
    </a>
  );
}

function ClerkSignUpButton({
  cta,
  variant,
  source,
  surface,
  trailing,
}: {
  cta: MarketingPrimaryCtaConfig;
  variant: MarketingAccountCtaVariant;
  source: MarketingAccountCtaSource;
  surface?: 'public' | 'app';
  trailing?: ReactNode;
}) {
  const { openSignUp } = useClerk();
  return (
    <Button
      variant="unstyled"
      type="button"
      onClick={() => {
        trackPrimaryCta(cta, source, surface);
        void openSignUp({
          forceRedirectUrl: typeof window !== 'undefined' ? window.location.href : '/',
        });
      }}
      className={VARIANT_CLASS[variant]}
      data-okra-cta-variant={cta.variant}
      data-okra-cta-source={source}
    >
      <PrimaryCtaLabel cta={cta} />
      {trailing}
    </Button>
  );
}

function PrimaryCtaControl({
  cta,
  variant,
  source,
  surface,
  trailing,
}: {
  cta: MarketingPrimaryCtaConfig;
  variant: MarketingAccountCtaVariant;
  source: MarketingAccountCtaSource;
  surface?: 'public' | 'app';
  trailing?: ReactNode;
}) {
  if (cta.variant === 'sign_up' && isClerkEnabled()) {
    return (
      <ClerkSignUpButton
        cta={cta}
        variant={variant}
        source={source}
        surface={surface}
        trailing={trailing}
      />
    );
  }
  return (
    <PrimaryCtaAnchor
      cta={cta}
      variant={variant}
      source={source}
      surface={surface}
      trailing={trailing}
    />
  );
}

/** Primary marketing action for non-account slots such as the homepage hero. */
export function MarketingPrimaryCta({
  variant,
  source,
  surface,
  trailing,
  className,
}: {
  variant: MarketingAccountCtaVariant;
  source: MarketingAccountCtaSource;
  surface?: 'public' | 'app';
  trailing?: ReactNode;
  className?: string;
}) {
  const cta = getMarketingPrimaryCta();
  return (
    <span className={cn('inline-flex items-center', className)}>
      <PrimaryCtaControl
        cta={cta}
        variant={variant}
        source={source}
        surface={surface}
        trailing={trailing}
      />
    </span>
  );
}

/**
 * Fixed Sign Up action for surfaces whose primary action is not assigned by the
 * W.4.72 experiment (W.4.104: the landing hero and closing band). A signed-in
 * visitor gets a link to their documents instead of a second sign-up.
 */
export function MarketingSignUpCta({
  variant,
  source,
  trailing,
  className,
}: {
  variant: MarketingAccountCtaVariant;
  source: MarketingAccountCtaSource;
  trailing?: ReactNode;
  className?: string;
}) {
  const cta = getSignUpPrimaryCta();
  const signUp = <PrimaryCtaControl cta={cta} variant={variant} source={source} trailing={trailing} />;
  if (!isClerkEnabled()) {
    return <span className={cn('inline-flex items-center', className)}>{signUp}</span>;
  }
  const documentsHref = appUrl('/documents');
  return (
    <span className={cn('inline-flex items-center', className)}>
      <SignedOut>{signUp}</SignedOut>
      <SignedIn>
        <a
          href={documentsHref}
          onClick={() =>
            capture('marketing_home_cta_clicked', {
              cta: source === 'homepage_hero' ? 'hero_open_app' : 'footer_open_app',
              destination: documentsHref,
            })
          }
          className={VARIANT_CLASS[variant]}
        >
          Open your documents
          {trailing}
        </a>
      </SignedIn>
    </span>
  );
}

/**
 * Top-right account slot shared by the marketing header and studio landing top
 * bar. Signed-out visitors see the server-selected marketing action on public
 * pages; signed-in visitors keep the account menu. Non-apex surfaces retain
 * Sign Up because they do not receive the marketing experiment bootstrap.
 */
export function MarketingAccountCta({
  variant = 'marketing',
  source = 'marketing_header',
  surface,
  className,
  pin,
}: {
  variant?: MarketingAccountCtaVariant;
  source?: MarketingAccountCtaSource;
  surface?: 'public' | 'app';
  className?: string;
  /** Show Sign Up whatever the experiment assigned (W.4.104, desktop-surface header). */
  pin?: 'sign_up';
}) {
  const cta = pin === 'sign_up' ? getSignUpPrimaryCta() : getMarketingPrimaryCta();
  if (!isClerkEnabled()) {
    return (
      <span className={cn('inline-flex items-center', className)}>
        <PrimaryCtaControl cta={cta} variant={variant} source={source} surface={surface} />
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center', className)}>
      <SignedOut>
        <PrimaryCtaControl cta={cta} variant={variant} source={source} surface={surface} />
      </SignedOut>
      <SignedIn>
        <AccountMenu afterSignOutUrl="/" />
      </SignedIn>
    </span>
  );
}
